// components/AddAntifraudMethodForm.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  MenuItem,
  Stack,
  Divider,
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { useTypes } from "../hooks/useTypes";

const TIPOS = [{ value: 1, label: "Konduto" }];

const AMBIENTES = [
  { value: "sandbox", label: "Sandbox" },
  { value: "production", label: "Produção" },
];

/** Bases de status da Konduto para mapeamento de tipos */
const ANTIFRAUD_BASE_TITLES = {
  kdt_approved: "Konduto: aprovado",
  kdt_review: "Konduto: em análise",
  kdt_refused: "Konduto: reprovado",
  kdt_not_analyzed: "Konduto: não analisado",
};

/** Lista explícita das bases que queremos exibir */
const ANTIFRAUD_BASES = [
  "kdt_approved",
  "kdt_review",
  "kdt_refused",
  // "kdt_not_analyzed",
];

/** Helper de título genérico (mesmo padrão do AddPaymentMethodForm) */
const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

const AddAntifraudMethodForm = ({ method, onClose, onSubmit, isFetching }) => {
  const { userData } = useAuthContext();

  // Carrega árvore de tipos (para mapeamento de status da Konduto)
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rootTypes = typesData?.types || [];

  const [formData, setFormData] = useState(() => ({
    id: method?.id || undefined,
    id_usuario: userData?.id || null,
    id_usuario_ultima_alteracao: userData?.id || null,
    nome: "",
    descricao: "",
    status: 1,
    tipo: 1, // Konduto
    ambiente: "sandbox",
    api_key: "",
    api_secret: "",
    merchant_id: "",
    webhook_secret: "",
    score_threshold_aprovar: "",
    score_threshold_reprovar: "",
    timeout_ms: "",
    tentativas: "",
    custo_por_transacao: "",
    custo_mensal: "",
    json: "",

    // ===== Mapeamentos de status da Konduto → Tipo/Subtipos =====
    // APROVADO
    kdt_approved_type_id: null,
    kdt_approved_type1_id: null,
    kdt_approved_type2_id: null,
    kdt_approved_type3_id: null,
    // EM ANÁLISE
    kdt_review_type_id: null,
    kdt_review_type1_id: null,
    kdt_review_type2_id: null,
    kdt_review_type3_id: null,
    // REPROVADO
    kdt_refused_type_id: null,
    kdt_refused_type1_id: null,
    kdt_refused_type2_id: null,
    kdt_refused_type3_id: null,
    // NÃO ANALISADO
    kdt_not_analyzed_type_id: null,
    kdt_not_analyzed_type1_id: null,
    kdt_not_analyzed_type2_id: null,
    kdt_not_analyzed_type3_id: null,
  }));
  const [loadingById, setLoadingById] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Carregar completo ao editar
  useEffect(() => {
    const id = method?.id;
    if (!id) return;
    setLoadingById(true);
    setLoadError(null);
    fetchData(`/antifraud-methods/${id}`, "GET")
      .then((resp) => {
        const d = resp?.data || {};
        setFormData((prev) => ({
          ...prev,
          ...d,
          // nunca reidrata segredos em claro; usa apenas flags has_*
          api_key: "",
          api_secret: "",
          webhook_secret: "",
          id,
          id_usuario: d.id_usuario ?? prev.id_usuario ?? userData?.id ?? null,
          id_usuario_ultima_alteracao:
            userData?.id ?? prev.id_usuario_ultima_alteracao ?? null,
        }));
      })
      .catch((e) =>
        setLoadError(
          e?.response?.data?.message || "Falha ao carregar método."
        )
      )
      .finally(() => setLoadingById(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method?.id]);

  const isTipo = (t) => Number(formData.tipo) === Number(t);

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const maskSecretField = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const validate = () => {
    if (!String(formData.nome).trim()) return "Informe o nome.";
    if (![0, 1].includes(Number(formData.status)))
      return "Status inválido.";
    // Regras por tipo (mínimas)
    if (isTipo(1)) {
      // Konduto
      const hasApiKey = hasStoredSecret("api_key");
      if (!String(formData.api_key).trim() && !hasApiKey)
        return "api_key (Konduto) é obrigatória.";
    }
    return null;
  };

  const numOrNull = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);
  const intOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const normalizePayload = () => {
    const p = { ...formData };
    p.status = Number(p.status) === 1 ? 1 : 0;
    p.tipo = Number(p.tipo) || 1;
    p.ambiente = p.ambiente === "production" ? "production" : "sandbox";

    p.score_threshold_aprovar = numOrNull(p.score_threshold_aprovar);
    p.score_threshold_reprovar = numOrNull(p.score_threshold_reprovar);
    p.timeout_ms = intOrNull(p.timeout_ms);
    p.tentativas = intOrNull(p.tentativas);
    p.custo_por_transacao = numOrNull(p.custo_por_transacao);
    p.custo_mensal = numOrNull(p.custo_mensal);
    if (!p.json) p.json = null;

    // Normaliza todos os campos *_type_id / *_type[1-3]_id para number|null
    Object.keys(p).forEach((k) => {
      if (/_type(?:[123])?_id$/.test(k)) {
        const v = p[k];
        if (v === "" || v === null || typeof v === "undefined") {
          p[k] = null;
        } else {
          const n = Number(v);
          p[k] = Number.isFinite(n) ? n : null;
        }
      }
    });

    return p;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const err = validate();
    if (err) {
      setErrorMessage(err);
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit(normalizePayload());
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Erro ao salvar antifraude."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentLoading = loadingById && formData?.id;

  /** ======= Helpers de cascata de tipos para status da Konduto ======= */
  const getCascadeLevels = (base) => {
    const rootVal = formData[`${base}_type_id`];
    const root = rootTypes.find(
      (t) => Number(t.id) === Number(rootVal)
    );
    const l1 = Array.isArray(root?.children) ? root.children : [];

    const sub1Val = formData[`${base}_type1_id`];
    const n1 = l1.find((s) => Number(s.id) === Number(sub1Val));
    const l2 = Array.isArray(n1?.children) ? n1.children : [];

    const sub2Val = formData[`${base}_type2_id`];
    const n2 = l2.find((u) => Number(u.id) === Number(sub2Val));
    const l3 = Array.isArray(n2?.children) ? n2.children : [];

    return { l1, l2, l3 };
  };

  const handleCascadeChange = (base, level, val) => {
    setFormData((prev) => {
      const next = { ...prev };
      if (level === 0) {
        next[`${base}_type_id`] = val === "" ? null : val;
        next[`${base}_type1_id`] = null;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 1) {
        next[`${base}_type1_id`] = val === "" ? null : val;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 2) {
        next[`${base}_type2_id`] = val === "" ? null : val;
        next[`${base}_type3_id`] = null;
      }
      if (level === 3) {
        next[`${base}_type3_id`] = val === "" ? null : val;
      }
      return next;
    });
  };

  const renderCascade = (base) => {
    const titulo = ANTIFRAUD_BASE_TITLES[base] ?? base;
    const { l1, l2, l3 } = getCascadeLevels(base);

    return (
      <React.Fragment key={base}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {titulo}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Tipo"
            value={formData[`${base}_type_id`] ?? ""}
            onChange={(e) => handleCascadeChange(base, 0, e.target.value)}
            disabled={loadingTypes}
          >
            <MenuItem value="">—</MenuItem>
            {!loadingTypes &&
              rootTypes.map((t) => (
                <MenuItem key={`${base}-root-${t.id}`} value={t.id}>
                  {titleOf(t)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {l1.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 1"
              value={formData[`${base}_type1_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 1, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l1.map((s) => (
                <MenuItem key={`${base}-l1-${s.id}`} value={s.id}>
                  {titleOf(s)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l2.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 2"
              value={formData[`${base}_type2_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 2, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l2.map((u) => (
                <MenuItem key={`${base}-l2-${u.id}`} value={u.id}>
                  {titleOf(u)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l3.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 3"
              value={formData[`${base}_type3_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 3, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l3.map((v) => (
                <MenuItem key={`${base}-l3-${v.id}`} value={v.id}>
                  {titleOf(v)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </React.Fragment>
    );
  };

  /** Bases que realmente existem no formData (mantém flexível, igual payment) */
  const cascadeBases = useMemo(
    () =>
      ANTIFRAUD_BASES.filter((b) =>
        Object.prototype.hasOwnProperty.call(formData, `${b}_type_id`)
      ),
    [formData]
  );

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
      >
        <Typography variant="h6" gutterBottom>
          {formData?.id ? "Editar Antifraude" : "Adicionar Antifraude"}
        </Typography>
      </Stack>

      {loadError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}
      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {contentLoading ? (
        <Typography>Carregando…</Typography>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Básico */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome"
                name="nome"
                value={formData.nome || ""}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                <MenuItem value={1}>Ativo</MenuItem>
                <MenuItem value={0}>Inativo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descrição (opcional)"
                name="descricao"
                value={formData.descricao || ""}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>

            {/* Ambiente */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Ambiente"
                name="ambiente"
                value={formData.ambiente}
                onChange={handleChange}
                fullWidth
                select
              >
                {AMBIENTES.map((a) => (
                  <MenuItem key={a.value} value={a.value}>
                    {a.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Credenciais por tipo */}
            {isTipo(1) && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Konduto</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  {(() => {
                    const hasApiKey = hasStoredSecret("api_key");
                    const isMasked = hasApiKey && !formData.api_key;
                    return (
                      <TextField
                        label="API Key (Konduto)"
                        name="api_key"
                        value={isMasked ? "••••••••" : formData.api_key || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        required={!hasApiKey}
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.api_key && hasApiKey
                            ? "Já existe uma API Key configurada. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("api_key") : undefined}
                        onFocus={isMasked ? () => maskSecretField("api_key") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Merchant ID (opcional)"
                    name="merchant_id"
                    value={formData.merchant_id || ""}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {(() => {
                    const hasWebhook = hasStoredSecret("webhook_secret");
                    const isMasked = hasWebhook && !formData.webhook_secret;
                    return (
                      <TextField
                        label="Webhook Secret (opcional)"
                        name="webhook_secret"
                        value={isMasked ? "••••••••" : formData.webhook_secret || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.webhook_secret && hasWebhook
                            ? "Já existe um Webhook Secret configurado. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                        onFocus={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                      />
                    );
                  })()}
                </Grid>
              </>
            )}

            {isTipo(2) && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">ClearSale</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Key"
                    name="api_key"
                    value={formData.api_key || ""}
                    onChange={handleChange}
                    fullWidth
                    type="password"
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Secret"
                    name="api_secret"
                    value={formData.api_secret || ""}
                    onChange={handleChange}
                    fullWidth
                    type="password"
                    autoComplete="new-password"
                  />
                </Grid>
              </>
            )}

            {isTipo(3) && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">FControl</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Key"
                    name="api_key"
                    value={formData.api_key || ""}
                    onChange={handleChange}
                    fullWidth
                    type="password"
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Merchant ID"
                    name="merchant_id"
                    value={formData.merchant_id || ""}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {isTipo(9) && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Custom</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Key"
                    name="api_key"
                    value={formData.api_key || ""}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Secret"
                    name="api_secret"
                    value={formData.api_secret || ""}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Config JSON (opcional)"
                    name="json"
                    value={formData.json || ""}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
              </>
            )}

            {/* Parâmetros Operacionais/Custos */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Score para Aprovar"
                name="score_threshold_aprovar"
                value={formData.score_threshold_aprovar ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Score para Reprovar"
                name="score_threshold_reprovar"
                value={formData.score_threshold_reprovar ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Timeout (ms)"
                name="timeout_ms"
                value={formData.timeout_ms ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tentativas"
                name="tentativas"
                value={formData.tentativas ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Custo por Transação (R$)"
                name="custo_por_transacao"
                value={formData.custo_por_transacao ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Custo Mensal (R$)"
                name="custo_mensal"
                value={formData.custo_mensal ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            {/* ======= Mapeamento de tipos por status da Konduto ======= */}
            {cascadeBases.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">
                    Mapeamento de status da Konduto
                  </Divider>
                </Grid>
                {cascadeBases.map((base) => renderCascade(base))}
              </>
            )}
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting || isFetching}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || isFetching}
            >
              {isFetching || isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Salvar"
              )}
            </Button>
          </Box>
        </form>
      )}
    </Paper>
  );
};

export default AddAntifraudMethodForm;
